import Vue, { CreateElement } from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { Body1, H6, Subtitle1 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Card } from '@src/components/card/card'
import { Modal } from '@src/elements/modal/modal'
import { MenuButton } from '@src/elements/button/button'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'
import { TrainingDay } from '@src/data/training-plan'
import WorkoutData from '@src/data/workout-data'
import SessionManager from '@src/lib/session_manager'
import { ExerciseCardConfig } from '@src/components/card/exercise-card/exercise-card-config/exercise-card-config'
import Club from '@src/data/club'
import { HealthLimitationSet } from '@src/data/health-limitation-set'

import '../workout-card.sass'

@Component({
  computed: {
    club () {
      return this.$store.state.club
    }
  }
})
export class WorkoutCardConfig extends Vue {

  @Prop({ required: true })
  dayNumber: number

  @Prop({ required: true })
  removeWorkoutCallback: (day: TrainingDay) => void

  @Prop({ required: true })
  removeDayCallback: (day: TrainingDay) => void

  @Prop({ required: true })
  day: TrainingDay

  @Prop({ required: true })
  healthLimitations: HealthLimitationSet

  @Prop({ required: false, default: false })
  hidden: boolean

  protected showEquipmentPopup: boolean = false
  protected showExercisesPopup: boolean = false
  private club: Club
  private workout: WorkoutData | null = null

  constructor (props) {
    super(props)
  }

  created () {
    return this.setWorkout()
  }

  @Watch('day')
  setWorkout () {
    this.workout = this.day.workoutAsArray[0]
  }

  render (h: CreateElement) {
    if (!this.workout) {
      return null
    }
    let setDisable = (this.day.immutable && !this.showExercisesPopup && !this.showEquipmentPopup)
    return <Card class={`workout-card workout-card-config ${setDisable ? 'disabled' : ''} ${this.hidden ? 'hidden-workout' : ''}`}>
      {this.renderHeader(h)}
      {this.renderPreview(h)}
      {this.renderTitle(h)}
      {this.renderSpecs(h)}
      {this.renderRight(h)}
    </Card>
  }

  renderHeader (h: CreateElement) {
    return <div slot='header' class='header workout-config-header'>
      <H6 class='workout-day'>{this.$t('DAY')} {this.dayNumber}</H6>
    </div>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview' class='workout-preview-medium'>
      <img src={this.workout!.displayImage} />
    </div>
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title' slot='title'>
      {this.workout!.isCertified() ? <Icon name='workout/certified' /> : ''}
      {this.workout!.name}
    </H6>
  }

  renderSpecs (h: CreateElement) {
    const firstFiveEquipmentItems = this.workout!.equipment.slice(0, 5).map((equipmentName) => {
      return <div class='equipment'>
        <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}>
          <Icon name={'equipment/' + equipmentName} />
        </Tooltip>
      </div>
    })

    const allEquipmentItems = this.workout!.equipment.map((equipmentName) => {
      return <div class='equipment'>
        <Icon name={'equipment/' + equipmentName} />
        <Body1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}</Body1>
      </div>
    })

    const selectedExercises = this.workout!.workoutExercises.map((workoutExercise, index) => {
      const showBreakpointOption = this.workout!.continuousWorkout && index !== this.workout!.workoutExercises.length - 1
      return <ExerciseCardConfig type='strictView' workoutExercise={workoutExercise} index={index} id={'exercise' + index} club={this.club} showBreakpointOption = {showBreakpointOption} healthLimitations={this.healthLimitations}
      />
    })

    return <div slot='specs' class='specs'>
      <div class='workout-detail'>
        <Icon name='workout/exercises' />
        <Subtitle1 class='detail-text'>{this.workout!.exerciseCount} {this.$t('EXERCISES')}</Subtitle1>
      </div>
      <div class='workout-detail'>
        <Icon name='workout/time' />
        <Subtitle1 class='detail-text'>{`${this.workout!.estimatedDurationInMin} ${this.$t('UNITS.MIN')}`}</Subtitle1>
      </div>
      <div class='workout-detail'>
        <Icon name='workout/score' />
        <Subtitle1 class='detail-text'>{this.workout!.estimatedMetValue} {this.$t('UNITS.MET')}</Subtitle1>
      </div>

      <div class='equipmentWrapper'>{firstFiveEquipmentItems}{this.workout!.equipment.length > 5 ?
        <Icon class='see-more' name='util/see-more' click={this.invertShowEquipment} /> : ''} </div>
      <Modal class={'training-equipment-modal'} show={this.showEquipmentPopup} closeCallback={this.invertShowEquipment}
             title={this.$t('EQUIPMENT')}>
        {allEquipmentItems}
      </Modal>
      <Modal class='exercises-modal' show={this.showExercisesPopup} closeCallback={this.invertShowExercises}
             title={this.$t('EXERCISES')}>
        <div class={'exercises'}>
          {selectedExercises}
        </div>
      </Modal>
    </div>
  }

  renderRight (h: CreateElement) {
    return <div slot='right' class='more-options'>
      {this.hidden ?
        <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.HIDDEN_WORKOUT')}>
          <Icon class='hidden' name={'workout/hidden-workout'} />
        </Tooltip> : null}

      {this.day.immutable ? '' :
        <Tooltip content={this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.REMOVE_DAY_SLOT')}>
          <Icon name='util/minus' class='remove-day-btn' click={this.removeDay}/>
        </Tooltip>
      }
      <MenuButton>
        <Popover>
          <OptionGroup>
            <div ref='view-exercises-btn' onClick={this.showExercises}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.VIEW_EXERCISES')}</div>
            {!this.day.immutable ? <div class='remove-label' ref='remove-btn' onClick={this.removeWorkout}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.ELIMINATE_WORKOUT')}</div> : null}
          </OptionGroup>
        </Popover>
      </MenuButton>
    </div>
  }

  invertShowEquipment () {
    this.showEquipmentPopup = !this.showEquipmentPopup
  }

  invertShowExercises () {
    this.showExercisesPopup = !this.showExercisesPopup
  }

  removeWorkout () {
    this.removeWorkoutCallback(this.day)
  }

  removeDay () {
    this.removeDayCallback(this.day)
  }

  showExercises () {
    this.showExercisesPopup = true
  }

  async editWorkout () {
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/workout/edit/${this.workout!.id}`)
  }
}
